import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from './Navigation';

// Routes that should NOT show navigation
const noNavRoutes = ['/login', '/register', '/forgot-password'];
const noNavPrefixes = ['/reset-password', '/verify-email'];

// Routes exempt from profile completion check
const profileExemptRoutes = ['/login', '/register', '/forgot-password', '/settings', '/privacy', '/terms'];
const profileExemptPrefixes = ['/reset-password', '/verify-email', '/accept-invite', '/auth/', '/checkin/', '/schedule/shared'];

function shouldShowNav(pathname) {
  // Check exact matches
  if (noNavRoutes.includes(pathname)) {
    return false;
  }

  // Check prefix matches (for routes with parameters)
  for (const prefix of noNavPrefixes) {
    if (pathname.startsWith(prefix)) {
      return false;
    }
  }

  return true;
}

// Skip to main content link for keyboard users
function SkipLink() {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-500 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none"
    >
      Skip to main content
    </a>
  );
}

function isProfileExempt(pathname) {
  if (profileExemptRoutes.includes(pathname)) return true;
  for (const prefix of profileExemptPrefixes) {
    if (pathname.startsWith(prefix)) return true;
  }
  return false;
}

export default function Layout({ children }) {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const showNav = shouldShowNav(location.pathname);

  // Redirect authenticated users with incomplete profiles
  if (isAuthenticated && user && user.profile_completed === false && !isProfileExempt(location.pathname)) {
    return <Navigate to="/settings?setup=1" replace />;
  }

  return (
    <div className="min-h-screen bg-brand-light">
      {/* Skip navigation link for accessibility */}
      <SkipLink />

      {showNav && <Navigation />}

      {/* Main content area with id for skip link */}
      <main
        id="main-content"
        className="focus:outline-none"
        tabIndex={-1}
      >
        {children}
      </main>
    </div>
  );
}
